import { useEffect, useState } from "react";
import { Box, Grid, GridItem, Flex, Image, Text, InputGroup, InputLeftElement, Input, Button, Center, Spinner, Popover, PopoverTrigger, PopoverContent, PopoverBody, useDisclosure, } from "@chakra-ui/react";
import MyDiv from "./manualAttendanceList.style";
import { AlertCard, CustomTable, CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";
import { useNavigate } from "react-router-dom";
import * as routesNames from '../../../../constants/routes'
import ArrowIcon from '../../../../assets/images/ActionArrow.svg'
import { Column } from "../../../../components/CustomTable/customTable";
import { CalendarIcon, SearchIcon } from "@chakra-ui/icons";
import Calendar from "react-calendar";
import TimePicker from "react-time-picker";
import "react-time-picker/dist/TimePicker.css";
import "react-clock/dist/Clock.css";
interface ManualAttendanceListProps {
  summary: { total: number; present: number; absent: number; halfDay: number };
  setSummary: React.Dispatch<React.SetStateAction<{ total: number; present: number; absent: number; halfDay: number }>>;
}
const ManualAttendanceList = ({ summary, setSummary }: ManualAttendanceListProps) => {
  const { addToast } = CustomToast();
  const { fetchApi, loading } = useFetch(addToast);
  const navigate = useNavigate();
  const [employees, setEmployees] = useState<any[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [isFinalized, setIsFinalized] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [submitAlertOpen, setSubmitAlertOpen] = useState(false);
  const [tempTimes, setTempTimes] = useState<{ [key: number]: { inTime?: string; outTime?: string } }>({});
  const [timeAlertData, setTimeAlertData] = useState<{ id: number; type: "inTime" | "outTime"; newValue: string; oldValue: string; } | null>(null);
  const [alertData, setAlertData] = useState<{ id: number, status: string, label: string, shiftStart: string, shiftEnd: string } | null>(null);

  useEffect(() => {
    if (selectedDate) {
      EmployeeData();
    }
  }, [selectedDate]);

  const filteredEmployees = employees.filter((emp) =>
    emp.emp_Name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleClick = (employeeId: number) => {
    navigate(routesNames.ATTENDANCEDETAIL, { state: { employeeId: employeeId } });
  }

  const EmployeeData = async () => {
    try {
      const data = await fetchApi("Hrlense_Attendance/manualAttendance", "GET", null, `attendanceDate=${formatDate(selectedDate)}`);
      if (data) {
        setEmployees(data.employees || []);
        setSummary(data.summary || { total: 0, presence: 0, absence: 0, halfday: 0 });
        setIsFinalized(data.isFinalized);
      }
    } catch (error: any) {
      addToast({ message: error.message || "An error occurred", status: "error" });
    }
  };

  const updateSingleEmployee = async (employeeId: number, outTime: string, inTime: string) => {
    try {
      const payload = {
        attendanceDate: formatDate(selectedDate),
        employeeId,
        inTime,
        outTime
      }
      const response = await fetchApi("Hrlense_Attendance/Update", "PUT", payload);
      if (response) {
        EmployeeData();
        addToast({ message: "Employee attendance updated successfully", status: "success" });
      }
    } catch (err: any) {
      addToast({ message: err.message || "Something went wrong", status: "error" });
    }
  };

  const handleConfirmUpdate = async () => {
    if (timeAlertData) {
      const { id, type, newValue } = timeAlertData;
      const emp = employees.find(e => e.employeeID === id);
      if (!emp) return;

      // Update UI immediately
      setEmployees(prev =>
        prev.map(e =>
          e.employeeID === id
            ? type === "inTime"
              ? { ...e, shiftStart: newValue }
              : { ...e, shiftEnd: newValue }
            : e
        )
      );

      // Call API
      await updateSingleEmployee(id, type === "outTime" ? newValue : emp.shiftEnd || "", type === "inTime" ? newValue : emp.shiftStart || "");
      setTimeAlertData(null);
      setAlertOpen(false);
      EmployeeData();
      return;
    }

    // Existing status change logic
    if (alertData) {
      await updateSingleEmployee(alertData.id, alertData.shiftEnd, alertData.shiftStart);
      setAlertOpen(false);
      EmployeeData();
    }
  };

  const formatDate = (date: Date) => {
    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const handleSubmit = async () => {
    const missingIn = employees.filter(e => !e.shiftStart);
    const missingOut = employees.filter(e => !e.shiftEnd);

    if (missingIn.length > 0) {
      addToast({ message: `In-Time missing for: ${missingIn.map(e => e.emp_Name).join(", ")}`, status: "error" });
      return;
    }

    if (missingOut.length > 0) {
      addToast({ message: `Out-Time missing for: ${missingOut.map(e => e.emp_Name).join(", ")}`, status: "error" });
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        attendanceDate: formatDate(selectedDate),
        employees: employees.map(emp => ({
          employeeId: emp.employeeID,
          inTime: emp.shiftStart,
          outTime: emp.shiftEnd,
        }))
      };

      const response = await fetchApi("Hrlense_Attendance/manualAttendance", "POST", payload);
      if (response) {
        addToast({ message: "Attendance saved successfully", status: "success" });
        EmployeeData();
      }

    } catch (err: any) {
      addToast({ message: err.message || "Something went wrong", status: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleInTimeChange = (id: number, value: string) => {

    if (!value) {
      addToast({ message: "In-Time is required.", status: "error" });
      return;
    }
    const emp = employees.find(e => e.employeeID === id);
    if (!emp) return;

    if (isFinalized) {
      // show confirmation alert before updating
      setTimeAlertData({ id, type: "inTime", newValue: value, oldValue: emp.shiftStart || "", });
      setAlertOpen(true);
    } else {
      // directly update if not finalized
      setEmployees(prev =>
        prev.map(e =>
          e.employeeID === id ? { ...e, shiftStart: value } : e
        )
      );
    }
  };

  const handleOutTimeChange = (id: number, value: string) => {

    if (!value) {
      addToast({ message: "Out-Time is required.", status: "error" });
      return;
    }

    const emp = employees.find(e => e.employeeID === id);
    if (!emp) return;

    if (isFinalized) {
      // show confirmation alert before updating
      setTimeAlertData({
        id,
        type: "outTime",
        newValue: value,
        oldValue: emp.shiftEnd || "",
      });
      setAlertOpen(true);
    } else {
      // directly update if not finalized
      setEmployees(prev =>
        prev.map(e =>
          e.employeeID === id ? { ...e, shiftEnd: value } : e
        )
      );
    }
  };

  return (
    <MyDiv>
      <Box className="page_wrapper ">
        <Flex className="page_heading flex_header">
          <Text className="font-poppins text_medium text_lg">Attendance List</Text>
          <Flex className="filter_section">
            <InputGroup maxW="200px">
              <InputLeftElement pointerEvents="none">
                <SearchIcon color="gray.400" />
              </InputLeftElement>
              <Input placeholder="Search name" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
            </InputGroup>
            <Popover isOpen={isOpen} onOpen={onOpen} onClose={onClose} placement="bottom-end">
              <PopoverTrigger>
                <Button leftIcon={<CalendarIcon />} padding={1} variant="outline">
                  <Text className="font-poppins text_sm text_regular">{selectedDate.toDateString()}</Text>
                </Button>
              </PopoverTrigger>
              <PopoverContent w="auto">
                <PopoverBody p={2} display="flex" justifyContent="center">
                  <Calendar value={selectedDate} onChange={(value) => {
                    if (value instanceof Date) {
                      setSelectedDate(value);
                      onClose();
                    }
                  }}
                    maxDate={new Date()}
                  />
                </PopoverBody>
              </PopoverContent>
            </Popover>

            {isFinalized ? (
              <Text className="font-poppins text_sm text_medium" color="green" mt={3}>Attendance has been finalized.</Text>
            ) : (
              <Button className="font-poppins text_sm text_md" bg="#006838" variant="solid" color="#fff" _hover={{ bg: '#005529' }} onClick={() => setSubmitAlertOpen(true)} isLoading={loading}>Submit</Button>
            )}
          </Flex>
        </Flex>

        <Box position="relative" minH="300px">
          {submitting ? (
            <Center position="absolute" top={0} left={0} right={0} bottom={0}>
              <Box bg="#ffffff" border="1px solid #d1fae5" px={8} py={5} rounded="lg" boxShadow="0px 4px 12px rgba(0, 0, 0, 0.05)" textAlign="center">
                <Text className="font-poppins text_md text_semibold" color="#166534">Saving Attendance, Please Wait...</Text>
                <Text className="font-poppins text_sm text_regular" color="gray.600" mt={1}>This may take a few seconds.</Text>
              </Box>
            </Center>
          ) : loading ? (
            <Center position="absolute" top={0} left={0} right={0} bottom={0}>
              <Spinner thickness="4px" speed="0.65s" emptyColor="gray.200" color="#006838" size="xl" />
            </Center>
          ) : (
            <>
              <Grid className="grid_container">
                <GridItem colSpan={12}>
                  <CustomTable value={filteredEmployees} emptyMessage="No Data Available">
                    <Column header="S. No" body={(_, index) => index + 1} />
                    <Column header="Emp. Code" field="employee_Code" />
                    <Column header="Name" field="emp_Name" />
                    <Column header="Company" field="company_Name" />
                    <Column header="Branch" field="branch_Name" />
                    <Column header="Department" field="departmentName" />
                    <Column header="In-Time" body={(row: any) => (
                      <Box maxW="120px">
                        <TimePicker onChange={(value: string | null) => {
                          setTempTimes((prev) => ({
                            ...prev,
                            [row.employeeID]: {
                              ...(prev[row.employeeID] || {}),
                              inTime: value || "",
                            },
                          }));
                        }}
                          onBlur={() => {
                            const newValue = tempTimes[row.employeeID]?.inTime;
                            if (newValue && newValue !== row.shiftStart) {
                              handleInTimeChange(row.employeeID, newValue);
                            }
                          }}
                          value={tempTimes[row.employeeID]?.inTime ?? (row.shiftStart || "")}
                          disableClock={true}
                          format="HH:mm"
                          clearIcon={null}
                          clockIcon={null}
                        />
                      </Box>
                    )}
                    />
                    <Column header="Out-Time" body={(row: any) => (
                      <Box maxW="120px">
                        <TimePicker onChange={(value: string | null) => {
                          setTempTimes((prev) => ({
                            ...prev,
                            [row.employeeID]: {
                              ...(prev[row.employeeID] || {}),
                              outTime: value || "",
                            },
                          }));
                        }}
                          onBlur={() => {
                            const newValue = tempTimes[row.employeeID]?.outTime;
                            if (newValue && newValue !== row.shiftEnd) {
                              handleOutTimeChange(row.employeeID, newValue);
                            }
                          }}
                          value={tempTimes[row.employeeID]?.outTime ?? (row.shiftEnd || "")}
                          disableClock={true}
                          format="HH:mm"
                          clearIcon={null}
                          clockIcon={null}
                        />
                      </Box>
                    )}
                    />
                    <Column header="Status" body={(row: any) => {
                      const status = row.statusName?.toLowerCase() || "";
                      const statusStyles: Record<string, { bg: string; color: string }> = {
                        present: { bg: "#dcfce7", color: "#166534" },
                        absent: { bg: "#fee2e2", color: "#991b1b" },
                        "half day": { bg: "#fef3c7", color: "#92400e" },
                        late: { bg: "#fef9c3", color: "#854d0e" },
                        earlyout: { bg: "#e0f2fe", color: "#075985" },
                        punchinmissing: { bg: "#f3e8ff", color: "#6b21a8" },
                        punchoutmissing: { bg: "#fae8ff", color: "#86198f" },
                        "week off": { bg: "#f0fdf4", color: "#166534" },
                      };
                      const { bg, color } = statusStyles[status] || {
                        bg: "#f1f5f9",
                        color: "#475569",
                      };

                      return (
                        <Box px={3} py={1} borderRadius="md" textAlign="center" fontSize="sm" fontWeight="500" bg={bg} color={color} className="font-poppins">
                          {row.statusName || "—"}
                        </Box>
                      );
                    }}
                    />
                    <Column header="Action" body={(row: any) => (<Image src={ArrowIcon} alt="Action" onClick={() => handleClick(row.employeeID)} cursor="pointer" />)} />
                  </CustomTable>
                </GridItem>
              </Grid>
            </>)}
        </Box>
      </Box>
      <AlertCard
        isOpen={alertOpen}
        onClose={() => {
          setAlertOpen(false);
          setTimeAlertData(null);
        }}
        onConfirm={handleConfirmUpdate}
        title={
          timeAlertData
            ? "Confirm Time Update"
            : "Update Attendance"
        }
        description={
          timeAlertData
            ? `Are you sure you want to update ${timeAlertData.type === "inTime" ? "In-Time" : "Out-Time"
            } to ${timeAlertData.newValue}?`
            : `Are you sure you want to mark this employee as ${alertData?.label}?`
        }
        confirmLabel="Yes, Update"
        colorScheme="green"
      />

      <AlertCard
        isOpen={submitAlertOpen}
        onClose={() => setSubmitAlertOpen(false)}
        onConfirm={() => {
          setSubmitAlertOpen(false);
          handleSubmit();
        }}
        title="Confirm Attendance"
        description="Are you sure you want to submit today's attendance?"
        confirmLabel="Yes, Submit"
        colorScheme="green"
      />
    </MyDiv>
  );
};

export default ManualAttendanceList;
